/**
 * Touch control adapter registry + the default logging stub.
 *
 * The stub is the ONLY adapter the prototype ships: it records intents to the
 * game logger and performs no gameplay action (no synthetic keyboard events,
 * no InputSystem calls, no network traffic). A future lane swaps in a real
 * adapter with `setTouchControlAdapter()`; see DESIGN.md §4 for the proposed
 * integration seam.
 */
import { gameLogger } from '../../../utils/GameLogger';
import { formatMoveIntent } from './joystickMath';
import type { TouchButtonPhase, TouchControlAdapter, TouchMoveIntent } from './types';

/** Direction bucket used to keep move logs meaningful instead of per-pixel. */
function moveBucket(intent: TouchMoveIntent): string {
  const qx = Math.round(intent.x * 2) / 2;
  const qy = Math.round(intent.y * 2) / 2;
  return `${qx},${qy}`;
}

function logPhase(kind: 'ability' | 'attack', detail: string, phase: TouchButtonPhase): void {
  gameLogger.info('input', `[TouchControls] stub ${kind} ${detail} ${phase}`);
}

/**
 * Default adapter: logs every discrete edge; move updates are bucketed so a
 * drag cannot flood the console. Replace via `setTouchControlAdapter()`.
 */
export function createStubTouchAdapter(id = 'stub'): TouchControlAdapter {
  let lastBucket: string | null = null;
  return {
    id,
    onMove(intent) {
      const bucket = moveBucket(intent);
      if (bucket !== lastBucket) {
        lastBucket = bucket;
        gameLogger.debug('input', `[TouchControls] stub move ${formatMoveIntent(intent)} (${bucket})`);
      }
    },
    onMoveEnd() {
      lastBucket = null;
      gameLogger.info('input', '[TouchControls] stub move end');
    },
    onAbility(slot, phase) {
      logPhase('ability', `slot ${slot}`, phase);
    },
    onAttack(phase) {
      logPhase('attack', 'basic', phase);
    },
  };
}

let activeAdapter: TouchControlAdapter = createStubTouchAdapter();

/** Adapter the mounted shell should use. Never null; defaults to the stub. */
export function getTouchControlAdapter(): TouchControlAdapter {
  return activeAdapter;
}

/**
 * Install a real adapter (engine boot / integration lane), or `null` to
 * restore the logging stub.
 */
export function setTouchControlAdapter(adapter: TouchControlAdapter | null): void {
  activeAdapter = adapter ?? createStubTouchAdapter();
  gameLogger.info('input', `[TouchControls] adapter = ${activeAdapter.id}`);
}

/** Test-only reset; production state changes only through `set...`. */
export function resetTouchControlAdapterForTests(): void {
  activeAdapter = createStubTouchAdapter();
}
